import { api, ApiError } from '../../services/api.js';
import { navigate } from '../../router.js';
import { gameState } from '../../state/gameState.js';
import { requestPushPermission, subscribeToPush, getPushPermissionState } from '../../services/push.service.js';
import { isIOS, isStandalone } from '../../components/pwa-install-hint.js';
import { getSoundVolumePercent, setSoundVolumePercent } from '../../services/sound.service.js';
import { applyPrestigeTheme, PRESTIGE_THEME_MIN_RANK } from '../../utils/prestige-theme.js';

interface TwitchStatus {
  connected: boolean;
  connectedAt: string | null;
}

let consentChecked = false;

// ─── Push-Benachrichtigungen (NT-PUSH-PREFS-UI-01, Push-Kette 3/5) ────────────
//
// Kategorie-Keys + Reihenfolge müssen exakt PUSH_CATEGORIES aus
// apps/backend/src/modules/push/push.constants.ts spiegeln (etabliertes „kein Shared-Import
// für reine Anzeige-Werte"-Pattern, siehe BUG_REPORT_DISCORD_TAGS oben). PUSH_CATEGORIES selbst
// ist backend-seitig authoritativ (4/5 Dispatch + 5/5 Triggers sind reine Backend-Konsumenten).
const PUSH_CATEGORY_DEFS: { key: string; label: string; info: string }[] = [
  { key: 'jobs', label: 'JOBS', info: 'Meldet, wenn der letzte Job in der Warteschlange fertig ist.' },
  { key: 'greenhouse', label: 'GEWÄCHSHAUS', info: 'Meldet, wenn Pflanzen erntereif sind oder gegossen werden müssen.' },
  { key: 'lab', label: 'LABOR', info: 'Meldet, wenn ein Laborprodukt fertig ist und eingesammelt werden kann.' },
  { key: 'dealer', label: 'DEALER', info: 'Meldet, wenn der Dealer keinen Nachschub mehr hat und nichts verkauft.' },
  { key: 'transport', label: 'TRANSPORT', info: 'Meldet, wenn eine Lieferung im Lagerhaus angekommen ist.' },
  { key: 'darknet', label: 'DARKNET', info: 'Meldet, wenn eine Darknet-Order bald abläuft.' },
  { key: 'schwarzmarkt', label: 'SCHWARZMARKT', info: 'Meldet, wenn ein Angebot auf deine Annahme wartet.' },
  { key: 'laundry', label: 'GELDWÄSCHE', info: 'Meldet, wenn ein Geldwäsche-Zyklus abgeschlossen ist.' },
  { key: 'jail_property', label: 'KNAST & AUSBAU', info: 'Meldet, wenn deine Entlassung ansteht oder ein Ausbau fertig ist.' },
];

interface PushPrefs {
  master: boolean;
  [category: string]: boolean;
}

let pushPrefsState: PushPrefs | null = null;
// Einzelne Kategorien sind standardmäßig eingeklappt — "Alle Benachrichtigungen" (Master) bleibt
// davon unberührt immer sichtbar. Wird bei jedem Seitenaufruf zurückgesetzt (renderSettings()),
// analog zum FORMULARE-Akkordeon (openFormEntryId).
let pushCategoriesExpanded = false;

// "ALLE BENACHRICHTIGUNGEN" ist ein reiner Sammel-Schalter ohne eigene Gate-Wirkung — ob
// tatsächlich gesendet wird, hängt nur vom jeweiligen Kategorie-Flag ab (siehe
// isPushCategoryActive() im Backend, das seit dieser Iteration ebenfalls nur noch die
// Kategorie prüft, nicht mehr `master &&`). `master` bleibt im gespeicherten Objekt erhalten,
// wird aber rein informativ mitgeschickt/angezeigt.
function isAnyPushCategoryEffectivelyActive(prefs: PushPrefs): boolean {
  return PUSH_CATEGORY_DEFS.some((c) => prefs[c.key] === true);
}

function allPushCategoriesActive(prefs: PushPrefs): boolean {
  return PUSH_CATEGORY_DEFS.every((c) => prefs[c.key] === true);
}

// FORMULARE-Akkordeon (NT-OPTIONEN-FORMULARE-01): exklusives Aufklappen –
// nur ein Eintrag gleichzeitig offen.
type FormsEntryId = 'bug' | 'kontakt' | 'datenschutz';
let openFormEntryId: FormsEntryId | null = null;

// Discord-Forum-Tags des Bug-Report-Kanals (NT-SUPPORT-BUGREPORT-01) – muss exakt
// BUG_REPORT_DISCORD_TAGS aus apps/backend/src/modules/support/bug-report.constants.ts
// spiegeln. Mind. 1 Tag ist Pflicht (Forum-Vorgabe), Mehrfachauswahl möglich.
const BUG_REPORT_DISCORD_TAGS = [
  'User Interface',
  'Logic',
  'Text',
  'Jobs',
  'Talentbaum',
  'Dashboard',
  'Character',
  'Desktop',
  'Handy',
  'Tablet',
  'Events',
  'Crime',
  'Geld',
  'Schwarzgeld',
  'Scheine',
  'Timer',
  'Cooldown',
] as const;

function addStyles(): void {
  const existing = document.getElementById('settings-styles');
  const el = existing ?? document.createElement('style');
  el.id = 'settings-styles';
  el.textContent = `
    .settings-page {
      max-width: 700px;
      margin: 0 auto;
      padding: 2rem 1rem 4rem;
      font-family: 'Share Tech Mono', 'Courier New', monospace;
      color: var(--text-primary);
    }
    .settings-title {
      font-size: var(--font-size-xl);
      text-transform: uppercase;
      color: var(--text-primary);
      margin-bottom: 2rem;
      letter-spacing: 0.08em;
    }
    .settings-back {
      background: none;
      border: 1px solid var(--border);
      color: var(--text-muted);
      font-family: inherit;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      padding: 0.4rem 0.8rem;
      border-radius: var(--radius-btn);
      cursor: pointer;
      margin-bottom: 1.5rem;
      display: inline-block;
    }
    .settings-back:hover { color: var(--text-primary); border-color: var(--text-muted); }
    .settings-section {
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }
    .settings-section-title {
      font-size: var(--font-size-base);
      text-transform: uppercase;
      color: var(--text-primary);
      letter-spacing: 0.06em;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid var(--accent-gold);
    }
    .settings-status-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      font-size: var(--font-size-sm);
    }
    .settings-status-label { color: var(--text-muted); text-transform: uppercase; font-size: var(--font-size-xs); }
    .settings-status-value { text-transform: uppercase; font-size: var(--font-size-sm); }
    .settings-status-value--connected { color: var(--accent-green); }
    .settings-status-value--disconnected { color: var(--text-muted); }
    .settings-meta { font-size: var(--font-size-xs); color: var(--text-muted); margin-bottom: 1rem; }
    .settings-consent-block { margin-bottom: 1rem; }
    .settings-consent-label {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      max-width: 480px;
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      cursor: pointer;
      line-height: 1.5;
    }
    .settings-consent-label input[type="checkbox"] { width: auto; flex-shrink: 0; margin-top: 2px; accent-color: var(--accent-green); cursor: pointer; }
    .settings-consent-label a { color: var(--accent-gold); text-decoration: underline; }
    .settings-btn {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      padding: 0.5rem 1.2rem;
      border-radius: var(--radius-btn);
      cursor: pointer;
      letter-spacing: 0.05em;
    }
    .settings-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .settings-btn:not(:disabled):hover { border-color: var(--text-muted); }
    .settings-btn--danger { border-color: var(--accent-red); color: var(--accent-red); }
    .settings-btn--danger:not(:disabled):hover { background: var(--accent-red); color: var(--bg-base); }
    .settings-hint {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin-top: 0.75rem;
      line-height: 1.5;
    }
    .settings-flash {
      padding: 0.6rem 1rem;
      border-radius: var(--radius);
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      margin-bottom: 1.5rem;
      letter-spacing: 0.04em;
    }
    .settings-flash--success { color: var(--accent-green); border: 1px solid var(--accent-green); background: transparent; }
    .settings-flash--error   { color: var(--accent-red);   border: 1px solid var(--accent-red);   background: transparent; }

    /* FORMULARE-Akkordeon (NT-OPTIONEN-FORMULARE-01) */
    .forms-entry {
      border: 1px solid var(--border);
      border-radius: var(--radius);
      margin-bottom: 0.75rem;
      overflow: hidden;
    }
    .forms-entry:last-child { margin-bottom: 0; }
    .forms-entry-header {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: linear-gradient(90deg, rgba(240, 180, 41, 0.06) 0%, transparent 100%);
      border: none;
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-sm);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 0.75rem 1rem;
      cursor: pointer;
      transition: background 0.2s;
    }
    .forms-entry-header:hover { color: var(--accent-gold); background: linear-gradient(90deg, rgba(240, 180, 41, 0.12) 0%, transparent 100%); }
    .forms-entry-indicator { color: var(--text-muted); font-size: var(--font-size-sm); }
    .forms-entry-body {
      display: none;
      padding: 1rem;
      border-top: 1px solid var(--border);
    }
    .forms-entry--open .forms-entry-body { display: block; }

    .privacy-subsection { margin-bottom: 1.5rem; }
    .privacy-subsection:last-child { margin-bottom: 0; }
    .privacy-subsection-title {
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      color: var(--accent-gold);
      letter-spacing: 0.05em;
      margin-bottom: 0.5rem;
    }
    .privacy-danger-zone { border-top: 1px solid var(--border); padding-top: 1.5rem; }
    .privacy-warning {
      border: 1px solid var(--accent-red);
      border-radius: var(--radius);
      padding: 1rem;
      margin-bottom: 1rem;
      color: var(--accent-red);
      font-size: var(--font-size-xs);
      line-height: 1.6;
    }
    .privacy-warning-title {
      text-transform: uppercase;
      font-size: var(--font-size-sm);
      margin-bottom: 0.5rem;
      letter-spacing: 0.05em;
    }
    .privacy-warning ul { margin: 0.5rem 0; padding-left: 1.2rem; }
    .privacy-password-input {
      display: block;
      width: 100%;
      max-width: 320px;
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-sm);
      padding: 0.5rem 0.75rem;
      border-radius: var(--radius);
      margin-bottom: 0.75rem;
    }
    .privacy-password-input:focus { outline: none; border-color: var(--accent-red); }

    /* BUG MELDEN Formular (NT-SUPPORT-BUGREPORT-01) */
    .settings-section:has(#bugreport-form) { background: linear-gradient(135deg, rgba(240, 180, 41, 0.03) 0%, transparent 100%); }
    .bugreport-field { margin-bottom: 1rem; }
    .bugreport-label {
      display: block;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      color: var(--text-muted);
      letter-spacing: 0.05em;
      margin-bottom: 0.4rem;
    }
    .bugreport-input,
    .bugreport-select,
    .bugreport-textarea {
      display: block;
      width: 100%;
      max-width: 480px;
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      color: var(--text-primary);
      font-family: inherit;
      font-size: var(--font-size-sm);
      padding: 0.5rem 0.75rem;
      border-radius: var(--radius);
    }
    .bugreport-input:focus,
    .bugreport-select:focus,
    .bugreport-textarea:focus { outline: none; border-color: var(--accent-gold); }
    .bugreport-textarea { resize: vertical; min-height: 80px; }
    .bugreport-honeypot {
      position: absolute;
      left: -9999px;
      width: 1px;
      height: 1px;
      overflow: hidden;
    }
    .bugreport-tags { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .bugreport-tag-checkbox {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      color: var(--text-muted);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 0.3rem 0.6rem;
      cursor: pointer;
      transition: all 0.15s;
    }
    .bugreport-tag-checkbox:hover { border-color: var(--accent-gold); color: var(--accent-gold); }
    .bugreport-tag-checkbox input[type="checkbox"] { width: auto; flex-shrink: 0; accent-color: var(--accent-gold); cursor: pointer; }
    .bugreport-tag-checkbox input[type="checkbox"]:checked { accent-color: var(--accent-gold); }
    .bugreport-tag-checkbox input[type="checkbox"]:checked + span { color: var(--accent-gold); }

    /* Benachrichtigungen (NT-PUSH-PREFS-UI-01) */
    .push-toggle-row {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      padding: 0.6rem 0;
      cursor: pointer;
    }
    .push-toggle-row--master {
      background: var(--bg-elevated);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 0.7rem 0.8rem;
      margin-bottom: 0.8rem;
    }
    .push-toggle-row--master .push-toggle-label {
      color: var(--accent-gold);
      font-weight: 700;
    }
    .push-toggle-row--master input[type="checkbox"] { accent-color: var(--accent-gold); }
    .push-toggle-row input[type="checkbox"] {
      width: auto;
      flex-shrink: 0;
      margin-top: 3px;
      accent-color: var(--accent-green);
      cursor: pointer;
    }
    /* Einzelne Kategorien — standardmäßig eingeklappt, "Alle Benachrichtigungen" bleibt
       davon unberührt immer sichtbar (siehe .push-toggle-row--master oben). */
    .push-categories-toggle {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: transparent;
      border: 1px solid var(--border);
      border-radius: var(--radius);
      color: var(--text-muted);
      font-family: inherit;
      font-size: var(--font-size-xs);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 0.55rem 0.75rem;
      cursor: pointer;
      transition: color 0.15s, border-color 0.15s;
    }
    .push-categories-toggle:hover { color: var(--accent-gold); border-color: var(--accent-gold); }
    .push-categories-arrow {
      display: inline-block;
      transition: transform 0.2s ease;
    }
    .push-categories--open .push-categories-arrow { transform: rotate(90deg); }
    .push-categories .push-toggle-list { display: none; margin-top: 0.6rem; }
    .push-categories--open .push-toggle-list { display: block; }
    .push-toggle-list .push-toggle-row {
      border-top: 1px solid var(--border);
      padding: 0.6rem 0;
    }
    .push-toggle-list .push-toggle-row:first-child { border-top: none; }
    .push-toggle-text { display: flex; flex-direction: column; gap: 0.15rem; }
    .push-toggle-label {
      font-size: var(--font-size-sm);
      text-transform: uppercase;
      color: var(--text-primary);
      letter-spacing: 0.04em;
    }
    .push-toggle-row--master .push-toggle-label { font-size: var(--font-size-base); }
    .push-toggle-info { font-size: var(--font-size-xs); color: var(--text-muted); }
    .push-toggle-warning { color: var(--accent-orange); }

    /* Sound-Lautstärke (Community-Wunsch) */
    .sound-volume-row {
      display: flex;
      align-items: center;
      gap: 0.9rem;
    }
    .sound-volume-slider {
      flex: 1 1 auto;
      min-width: 0;
      height: 4px;
      -webkit-appearance: none;
      appearance: none;
      background: var(--border);
      border-radius: var(--radius-sm);
      cursor: pointer;
      accent-color: var(--accent-gold);
    }
    .sound-volume-slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: var(--accent-gold);
      cursor: pointer;
    }
    .sound-volume-slider::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border: none;
      border-radius: 50%;
      background: var(--accent-gold);
      cursor: pointer;
    }
    .sound-volume-value {
      flex: 0 0 auto;
      width: 3.2rem;
      text-align: right;
      font-size: var(--font-size-sm);
      color: var(--text-primary);
    }
    .sound-volume-value--muted { color: var(--text-muted); }

    /* Discord-Einladung (NT-SUPPORT-BUGREPORT-01 Tag-Erweiterung) */
    .settings-section:has(.discord-invite-link) { background: linear-gradient(135deg, rgba(75, 123, 157, 0.04) 0%, transparent 100%); border: 1px solid rgba(75, 123, 157, 0.2); }
    .discord-invite-link { display: inline-block; text-decoration: none; background: rgba(75, 123, 157, 0.15); border-color: var(--accent-blue) !important; color: var(--accent-blue) !important; }
    .discord-invite-link:hover { background: rgba(75, 123, 157, 0.25); }
  `;
  if (!existing) document.head.appendChild(el);
}

function getFlashHtml(): string {
  const params = new URLSearchParams(window.location.search);
  const twitch = params.get('twitch');
  const reason = params.get('reason') ?? '';

  if (!twitch) return '';

  if (twitch === 'connected') {
    return `<div class="settings-flash settings-flash--success">Twitch-Account erfolgreich verknüpft</div>`;
  }

  const messages: Record<string, string> = {
    already_connected: 'Dieser Twitch-Account ist bereits mit einem anderen Account verknüpft',
    invalid_state:     'Verknüpfung fehlgeschlagen — bitte erneut versuchen',
    twitch_error:      'Twitch-Fehler — bitte später erneut versuchen',
    no_consent:        'Einwilligung erforderlich',
  };

  const msg = messages[reason] ?? 'Unbekannter Fehler';
  return `<div class="settings-flash settings-flash--error">${msg}</div>`;
}

function renderTwitchConnected(connectedAt: string | null): string {
  const dateStr = connectedAt
    ? new Date(connectedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

  return `
    <div class="settings-section">
      <div class="settings-section-title">Twitch-Verknüpfung</div>
      <div class="settings-status-row">
        <span class="settings-status-label">Status:</span>
        <span class="settings-status-value settings-status-value--connected">Verknüpft</span>
      </div>
      <div class="settings-meta">Verknüpft seit: ${dateStr}</div>
      <button class="settings-btn settings-btn--danger" id="twitch-unlink-btn">Verknüpfung aufheben</button>
    </div>
  `;
}

function renderTwitchDisconnected(): string {
  return `
    <div class="settings-section">
      <div class="settings-section-title">Twitch-Verknüpfung</div>
      <div class="settings-status-row">
        <span class="settings-status-label">Status:</span>
        <span class="settings-status-value settings-status-value--disconnected">Nicht verknüpft</span>
      </div>
      <div class="settings-consent-block">
        <label class="settings-consent-label">
          <input type="checkbox" id="twitch-consent-cb">
          <span>
            Ich stimme der Verknüpfung meines Twitch-Accounts mit Nighttrade zu.
            Meine Twitch-Nutzer-ID wird gespeichert.
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>
          </span>
        </label>
      </div>
      <button class="settings-btn" id="twitch-link-btn" disabled>Mit Twitch verknüpfen</button>
      <div class="settings-hint">
        Verknüpfe deinen Twitch-Account um Stream-Punkte zu sammeln
        und am Community-System teilzunehmen.
      </div>
    </div>
  `;
}

function bindTwitchDisconnected(): void {
  const cb = document.getElementById('twitch-consent-cb') as HTMLInputElement | null;
  const btn = document.getElementById('twitch-link-btn') as HTMLButtonElement | null;
  if (!cb || !btn) return;

  cb.addEventListener('change', () => {
    consentChecked = cb.checked;
    btn.disabled = !consentChecked;
  });

  btn.addEventListener('click', () => {
    if (!consentChecked) return;
    window.location.href = '/api/v1/auth/twitch/link?consent=true';
  });
}

function bindTwitchConnected(): void {
  const btn = document.getElementById('twitch-unlink-btn') as HTMLButtonElement | null;
  if (!btn) return;

  btn.addEventListener('click', () => {
    void (async () => {
      btn.disabled = true;
      btn.textContent = 'Wird aufgehoben...';
      try {
        await api.delete<{ message: string }>('/auth/twitch/unlink');
        navigate('/settings');
      } catch {
        btn.disabled = false;
        btn.textContent = 'Verknüpfung aufheben';
      }
    })();
  });
}

// URL-Parameter nach Auswertung aus History entfernen (kein doppeltes Flash nach Reload)
function cleanUrlParams(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.has('twitch')) {
    url.searchParams.delete('twitch');
    url.searchParams.delete('reason');
    window.history.replaceState({}, '', url.toString());
  }
}

// ─── Push-Benachrichtigungen (NT-PUSH-PREFS-UI-01) ────────────────────────────

function renderPushToggleRow(key: string, label: string, info: string, checked: boolean): string {
  return `
    <label class="push-toggle-row">
      <input type="checkbox" data-push-category="${key}" ${checked ? 'checked' : ''}>
      <span class="push-toggle-text">
        <span class="push-toggle-label">${label}</span>
        <span class="push-toggle-info">${info}</span>
      </span>
    </label>
  `;
}

// ─── Sound-Lautstärke (Community-Wunsch) ──────────────────────────────────────

function renderSoundSectionBody(): string {
  const percent = getSoundVolumePercent();
  return `
    <div class="sound-volume-row">
      <input
        type="range" min="0" max="100" step="5"
        id="sound-volume-slider" class="sound-volume-slider"
        value="${String(percent)}"
        aria-label="Sound-Lautstärke"
      >
      <span class="sound-volume-value${percent === 0 ? ' sound-volume-value--muted' : ''}" id="sound-volume-value">${String(percent)}%</span>
    </div>
    <div class="settings-hint">Lautstärke für Spiel-Sounds (z. B. der Benachrichtigungston bei Job-Ereignissen). Bei 0 % bleiben Sounds stumm. Gilt nur für dieses Gerät.</div>
  `;
}

// ─── Rotes Prestige-Theme (NT-PRESTIGE-02) ─────────────────────────────────────
// Nur für Spieler ab Prestige III sichtbar; der Server lehnt das Einschalten darunter zusätzlich ab.

function renderPrestigeThemeSection(): string {
  const player = gameState.getPlayer();
  if (player === null || (player.prestigeRank ?? 0) < PRESTIGE_THEME_MIN_RANK) return '';
  return `
    <div class="settings-section">
      <div class="settings-section-title">Prestige-Theme</div>
      <label class="push-toggle-row">
        <input type="checkbox" id="prestige-theme-toggle" ${player.prestigeTheme === true ? 'checked' : ''}>
        <span class="push-toggle-text">
          <span class="push-toggle-label">Rotes Theme</span>
        </span>
      </label>
      <div class="settings-hint">Färbt Rahmen und Flächen rot ein. Freigeschaltet mit Prestige III, gilt auf allen Geräten.</div>
    </div>
  `;
}

function bindPrestigeThemeSection(): void {
  const toggle = document.getElementById('prestige-theme-toggle') as HTMLInputElement | null;
  if (!toggle) return;
  toggle.addEventListener('change', () => {
    const enabled = toggle.checked;
    toggle.disabled = true;
    void api.post<{ prestigeTheme: boolean }>('/prestige/theme', { enabled })
      .then((res) => {
        const player = gameState.getPlayer();
        if (player !== null) gameState.setPlayer({ ...player, prestigeTheme: res.prestigeTheme });
        applyPrestigeTheme();
      })
      .catch(() => {
        toggle.checked = !enabled;
      })
      .finally(() => {
        toggle.disabled = false;
      });
  });
}

function bindSoundSection(): void {
  const slider = document.getElementById('sound-volume-slider') as HTMLInputElement | null;
  const valueLabel = document.getElementById('sound-volume-value');
  if (!slider) return;

  slider.addEventListener('input', () => {
    const percent = Number(slider.value);
    setSoundVolumePercent(percent);
    if (valueLabel) {
      valueLabel.textContent = `${String(percent)}%`;
      valueLabel.classList.toggle('sound-volume-value--muted', percent === 0);
    }
  });

  // Kurze Hörprobe nach dem Loslassen — nicht bei jedem input-Tick (kein Sound-Spam beim Ziehen).
  slider.addEventListener('change', () => {
    const percent = Number(slider.value);
    if (percent <= 0) return;
    try {
      const audio = new Audio('/sounds/decision_ping.mp3');
      audio.volume = percent / 100;
      void audio.play().catch(() => { /* Sound nicht verfügbar */ });
    } catch {
      // Kein Fehler wenn Audio nicht unterstützt
    }
  });
}

function renderPushSectionBody(prefs: PushPrefs): string {
  const permissionState = getPushPermissionState();
  const deniedHint = permissionState === 'denied'
    ? `<div class="settings-hint push-toggle-warning">Benachrichtigungen sind für diesen Browser blockiert. Erlaube sie in den Browser-Einstellungen für diese Seite, um sie zu erhalten.</div>`
    : '';
  const iosHint = isIOS() && !isStandalone()
    ? `<div class="settings-hint">Push funktioniert auf iPhone/iPad nur, wenn Nighttrade zum Home-Bildschirm hinzugefügt wurde.</div>`
    : '';

  return `
    ${deniedHint}
    ${iosHint}
    <label class="push-toggle-row push-toggle-row--master">
      <input type="checkbox" data-push-category="master" ${allPushCategoriesActive(prefs) ? 'checked' : ''}>
      <span class="push-toggle-text">
        <span class="push-toggle-label">Alle Benachrichtigungen</span>
      </span>
    </label>
    <div class="push-categories${pushCategoriesExpanded ? ' push-categories--open' : ''}">
      <button
        type="button"
        class="push-categories-toggle"
        id="push-categories-toggle"
        aria-expanded="${pushCategoriesExpanded ? 'true' : 'false'}"
      >
        <span>Einzelne Kategorien</span>
        <span class="push-categories-arrow" aria-hidden="true">&#9654;</span>
      </button>
      <div class="push-toggle-list">
        ${PUSH_CATEGORY_DEFS.map((c) => renderPushToggleRow(c.key, c.label, c.info, prefs[c.key] === true)).join('')}
      </div>
    </div>
    <div class="settings-hint" id="push-prefs-hint"></div>
  `;
}

/** Setzt die DOM-Checkboxen der 9 Kategorien auf den übergebenen Wert (Sammel-Klick auf Master). */
function setAllCategoryCheckboxes(value: boolean): void {
  document.querySelectorAll<HTMLInputElement>('.push-toggle-list input[data-push-category]').forEach((cb) => {
    cb.checked = value;
  });
}

/** Spiegelt den Master-Haken nach jeder Einzel-Kategorie-Änderung: an, nur wenn wirklich alle 9 an sind. */
function syncMasterCheckbox(): void {
  if (!pushPrefsState) return;
  const masterCheckbox = document.querySelector<HTMLInputElement>('input[data-push-category="master"]');
  if (masterCheckbox) masterCheckbox.checked = allPushCategoriesActive(pushPrefsState);
}

async function handlePushToggleChange(checkbox: HTMLInputElement): Promise<void> {
  if (!pushPrefsState) return;
  const key = checkbox.dataset.pushCategory;
  if (!key) return;

  const newValue = checkbox.checked;
  const hint = document.getElementById('push-prefs-hint');
  if (hint) hint.textContent = '';

  const isMaster = key === 'master';
  // "ALLE BENACHRICHTIGUNGEN" ist ein reiner Sammel-Schalter: ein Klick setzt alle 9
  // Kategorien auf einmal (an oder aus). Er hat sonst keine eigene Wirkung — sein Haken
  // ist rein ein Anzeige-Spiegel von "sind gerade alle 9 aktiv" (siehe allPushCategoriesActive()),
  // keine unabhängig gespeicherte Bedingung. Einzelne Kategorien bleiben davon unabhängig
  // individuell an-/abwählbar, ohne die jeweils anderen zu berühren.
  const update: Record<string, boolean> = isMaster
    ? Object.fromEntries(PUSH_CATEGORY_DEFS.map((c) => [c.key, newValue]))
    : { [key]: newValue };
  const candidate: PushPrefs = { ...pushPrefsState, ...update };

  const wasActive = isAnyPushCategoryEffectivelyActive(pushPrefsState);
  const willBeActive = isAnyPushCategoryEffectivelyActive(candidate);

  // Permission-on-first-toggle (Entscheidung E): nur anfragen, wenn diese Änderung den
  // Spieler von "keine Kategorie effektiv aktiv" zu "mindestens eine aktiv" bringt.
  if (!wasActive && willBeActive) {
    if (isIOS() && !isStandalone()) {
      checkbox.checked = !newValue;
      if (hint) hint.textContent = 'Push funktioniert auf iPhone/iPad nur als installierte App — zuerst über TEILEN → „Zum Home-Bildschirm" hinzufügen.';
      return;
    }

    const granted = await requestPushPermission();
    if (!granted) {
      checkbox.checked = !newValue;
      if (hint) hint.textContent = 'Benachrichtigungen wurden im Browser blockiert. Du kannst sie in den Browser-Einstellungen für diese Seite wieder erlauben.';
      return;
    }

    const subscribed = await subscribeToPush();
    if (!subscribed) {
      checkbox.checked = !newValue;
      if (hint) hint.textContent = 'Push konnte nicht aktiviert werden. Bitte später erneut versuchen.';
      return;
    }
  }

  checkbox.disabled = true;
  try {
    const res = await api.post<{ prefs: PushPrefs }>('/push/prefs', update);
    pushPrefsState = res.prefs;
    if (isMaster) {
      setAllCategoryCheckboxes(newValue);
    } else {
      syncMasterCheckbox();
    }
  } catch (err) {
    checkbox.checked = !newValue;
    if (hint) hint.textContent = err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen. Bitte später erneut versuchen.';
  } finally {
    checkbox.disabled = false;
  }
}

function bindPushSection(): void {
  document.querySelectorAll<HTMLInputElement>('[data-push-category]').forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      void handlePushToggleChange(checkbox);
    });
  });

  const toggleBtn = document.getElementById('push-categories-toggle');
  const wrapper = document.querySelector<HTMLElement>('.push-categories');
  if (toggleBtn && wrapper) {
    toggleBtn.addEventListener('click', () => {
      pushCategoriesExpanded = !pushCategoriesExpanded;
      wrapper.classList.toggle('push-categories--open', pushCategoriesExpanded);
      toggleBtn.setAttribute('aria-expanded', String(pushCategoriesExpanded));
    });
  }
}

// ─── FORMULARE-Sektion (NT-OPTIONEN-FORMULARE-01) ────────────────────────────

function renderDatenschutzBody(): string {
  return `
    <div class="privacy-subsection">
      <div class="privacy-subsection-title">Datenexport</div>
      <p class="settings-meta">Lade eine Kopie deiner gespeicherten Daten herunter (DSGVO Art. 15/20).</p>
      <button class="settings-btn" id="privacy-export-btn">Daten exportieren</button>
      <div class="settings-hint" id="privacy-export-hint"></div>
    </div>
    <div class="privacy-subsection privacy-danger-zone">
      <div class="privacy-warning">
        <div class="privacy-warning-title">Warnung — Account-Löschung</div>
        <p>Diese Aktion löscht deinen gesamten Nighttrade-Account unwiderruflich. Du verlierst:</p>
        <ul>
          <li>Deinen kompletten Spielfortschritt</li>
          <li>Alle Items, Scheine und Talente</li>
          <li>Deinen Zugang zum Spiel</li>
        </ul>
        <p>Es ist NICHT möglich, nur einzelne Daten zu löschen und weiterzuspielen. Eine Löschung kann nicht rückgängig gemacht werden.</p>
      </div>
      <input
        type="password"
        id="privacy-delete-password"
        class="privacy-password-input"
        placeholder="Passwort"
        autocomplete="current-password"
      >
      <button class="settings-btn settings-btn--danger" id="privacy-delete-btn" disabled>Account endgültig löschen</button>
      <div class="settings-hint" id="privacy-delete-hint"></div>
    </div>
  `;
}

function renderBugReportBody(): string {
  return `
    <form id="bugreport-form" novalidate>
      <div class="bugreport-field bugreport-honeypot" aria-hidden="true">
        <label for="bugreport-website">Website</label>
        <input type="text" id="bugreport-website" name="website" tabindex="-1" autocomplete="off">
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="bugreport-category">Kategorie</label>
        <select class="bugreport-select" id="bugreport-category" required>
          <option value="JOB_SYSTEM">Job-System</option>
          <option value="ECONOMY">Economy</option>
          <option value="UI_MOBILE">UI/Mobile</option>
          <option value="SONSTIGES">Sonstiges</option>
        </select>
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="bugreport-title">Titel</label>
        <input class="bugreport-input" type="text" id="bugreport-title" minlength="8" maxlength="120" required>
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="bugreport-description">Beschreibung</label>
        <textarea class="bugreport-textarea" id="bugreport-description" minlength="30" maxlength="2000" required></textarea>
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="bugreport-repro">Reproduktionsschritte (optional)</label>
        <textarea class="bugreport-textarea" id="bugreport-repro" minlength="15" maxlength="1000"></textarea>
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="bugreport-screenshot">Screenshot (optional, max. 5 MB, PNG/JPEG/WebP)</label>
        <input class="bugreport-input" type="file" id="bugreport-screenshot" accept="image/png,image/jpeg,image/webp">
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label">Tags (mind. 1 Pflicht)</label>
        <div class="bugreport-tags">
          ${BUG_REPORT_DISCORD_TAGS.map(
            (tag) => `
            <label class="bugreport-tag-checkbox">
              <input type="checkbox" name="tags" value="${tag}">
              <span>${tag}</span>
            </label>
          `,
          ).join('')}
        </div>
      </div>
      <button type="submit" class="settings-btn" id="bugreport-submit-btn">Bug-Report senden</button>
      <div class="settings-hint" id="bugreport-hint"></div>
    </form>
  `;
}

// Kategorien des Kontaktformulars (NT-SUPPORT-CONTACT-01) – muss exakt
// CONTACT_CATEGORY_LABELS aus apps/backend/src/modules/support/contact.constants.ts spiegeln.
const CONTACT_CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'ACCOUNT', label: 'Account/Login-Problem' },
  { value: 'PAYMENT', label: 'Zahlung/Rechnung' },
  { value: 'FEEDBACK', label: 'Feedback & Ideen' },
  { value: 'PRIVACY', label: 'Sonstiges/Datenschutzanfrage' },
];

function renderContactBody(): string {
  return `
    <form id="contact-form" novalidate>
      <div class="bugreport-field bugreport-honeypot" aria-hidden="true">
        <label for="contact-website">Website</label>
        <input type="text" id="contact-website" name="website" tabindex="-1" autocomplete="off">
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="contact-category">Kategorie</label>
        <select class="bugreport-select" id="contact-category" required>
          ${CONTACT_CATEGORY_OPTIONS.map((opt) => `<option value="${opt.value}">${opt.label}</option>`).join('')}
        </select>
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="contact-email">E-Mail für Rückantwort</label>
        <input class="bugreport-input" type="email" id="contact-email" maxlength="254" required>
        <p class="settings-meta">Wir speichern diese Adresse nicht dauerhaft — nur für die Antwort auf diese Anfrage.</p>
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="contact-subject">Betreff</label>
        <input class="bugreport-input" type="text" id="contact-subject" minlength="5" maxlength="120" required>
      </div>
      <div class="bugreport-field">
        <label class="bugreport-label" for="contact-message">Nachricht</label>
        <textarea class="bugreport-textarea" id="contact-message" minlength="20" maxlength="3000" required></textarea>
      </div>
      <button type="submit" class="settings-btn" id="contact-submit-btn">Nachricht senden</button>
      <div class="settings-hint" id="contact-hint"></div>
    </form>
  `;
}

function renderFormsEntry(id: FormsEntryId, label: string, bodyHtml: string): string {
  return `
    <div class="forms-entry" data-entry-id="${id}">
      <button type="button" class="forms-entry-header" data-forms-toggle="${id}">
        <span>${label}</span>
        <span class="forms-entry-indicator">+</span>
      </button>
      <div class="forms-entry-body" data-forms-body="${id}">${bodyHtml}</div>
    </div>
  `;
}

function renderDiscordInviteSection(): string {
  return `
    <div class="settings-section">
      <div class="settings-section-title">Discord</div>
      <p class="settings-meta">Tritt unserer Community bei — Ankündigungen, Austausch mit anderen Spielern und Diskussion über Bug-Reports.</p>
      <a class="settings-btn discord-invite-link" href="https://discord.gg/WvQvFcmnNy" target="_blank" rel="noopener noreferrer">Zum Discord</a>
    </div>
  `;
}

function renderFormsSection(): string {
  return `
    <div class="settings-section forms-section">
      <div class="settings-section-title">Formulare</div>
      <div class="forms-accordion">
        ${renderFormsEntry('bug', 'BUG MELDEN', renderBugReportBody())}
        ${renderFormsEntry('kontakt', 'KONTAKT', renderContactBody())}
        ${renderFormsEntry('datenschutz', 'DATENSCHUTZ', renderDatenschutzBody())}
      </div>
    </div>
  `;
}

function updateFormsEntryVisibility(): void {
  document.querySelectorAll<HTMLElement>('.forms-entry').forEach((entry) => {
    const isOpen = entry.dataset.entryId === openFormEntryId;
    entry.classList.toggle('forms-entry--open', isOpen);
    const indicator = entry.querySelector('.forms-entry-indicator');
    if (indicator) indicator.textContent = isOpen ? '-' : '+';
  });
}

function bindPrivacyExport(): void {
  const btn = document.getElementById('privacy-export-btn') as HTMLButtonElement | null;
  const hint = document.getElementById('privacy-export-hint');
  if (!btn || !hint) return;

  btn.addEventListener('click', () => {
    void (async () => {
      btn.disabled = true;
      hint.textContent = '';
      try {
        const exportData = await api.get<Record<string, unknown>>('/account/export');
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'nighttrade-daten-export.json';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        hint.textContent = 'Export wurde erstellt und heruntergeladen.';
      } catch (err) {
        hint.textContent = err instanceof ApiError ? err.message : 'Export fehlgeschlagen.';
      } finally {
        btn.disabled = false;
      }
    })();
  });
}

function bindPrivacyDelete(): void {
  const passwordInput = document.getElementById('privacy-delete-password') as HTMLInputElement | null;
  const deleteBtn = document.getElementById('privacy-delete-btn') as HTMLButtonElement | null;
  const hint = document.getElementById('privacy-delete-hint');
  if (!passwordInput || !deleteBtn || !hint) return;

  passwordInput.addEventListener('input', () => {
    deleteBtn.disabled = passwordInput.value.length === 0;
  });

  deleteBtn.addEventListener('click', () => {
    void (async () => {
      const password = passwordInput.value;
      if (!password) return;

      deleteBtn.disabled = true;
      hint.textContent = '';

      try {
        const verifyResult = await api.post<{ valid: boolean }>('/privacy/verify-password', { password });
        if (!verifyResult.valid) {
          hint.textContent = 'Passwort ist falsch.';
          deleteBtn.disabled = false;
          return;
        }

        await api.delete<{ message: string }>('/account/me', { password });
        gameState.clearPlayer();
        navigate('/');
      } catch (err) {
        hint.textContent = err instanceof ApiError ? err.message : 'Löschung fehlgeschlagen. Bitte später erneut versuchen.';
        deleteBtn.disabled = false;
      }
    })();
  });
}

function bindBugReportForm(): void {
  const form = document.getElementById('bugreport-form') as HTMLFormElement | null;
  const submitBtn = document.getElementById('bugreport-submit-btn') as HTMLButtonElement | null;
  const hint = document.getElementById('bugreport-hint');
  if (!form || !submitBtn || !hint) return;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      submitBtn.disabled = true;
      hint.textContent = '';

      const category = (document.getElementById('bugreport-category') as HTMLSelectElement).value;
      const title = (document.getElementById('bugreport-title') as HTMLInputElement).value;
      const description = (document.getElementById('bugreport-description') as HTMLTextAreaElement).value;
      const reproSteps = (document.getElementById('bugreport-repro') as HTMLTextAreaElement).value;
      const website = (document.getElementById('bugreport-website') as HTMLInputElement).value;
      const screenshotInput = document.getElementById('bugreport-screenshot') as HTMLInputElement;
      const screenshotFile = screenshotInput.files?.[0] ?? null;
      const selectedTags = Array.from(
        form.querySelectorAll<HTMLInputElement>('input[name="tags"]:checked'),
      ).map((checkbox) => checkbox.value);

      if (selectedTags.length === 0) {
        hint.textContent = 'Bitte mindestens einen Tag auswählen.';
        submitBtn.disabled = false;
        return;
      }

      // multipart/form-data statt JSON – api.ts-Wrapper unterstützt keine FormData-Bodies,
      // daher hier bewusst ein eigenständiger fetch() (CSRF-Header manuell gesetzt).
      const formData = new FormData();
      formData.append('website', website);
      formData.append('category', category);
      formData.append('title', title);
      formData.append('description', description);
      if (reproSteps) formData.append('reproSteps', reproSteps);
      if (screenshotFile) formData.append('screenshot', screenshotFile);
      selectedTags.forEach((tag) => {
        formData.append('tags', tag);
      });

      try {
        const csrfToken = gameState.getCsrfToken();
        const response = await fetch('/api/v1/support/bug-reports', {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
        });

        const resultBody = (await response.json()) as {
          data?: { message: string };
          error?: { message: string };
        };

        if (!response.ok) {
          hint.textContent = resultBody.error?.message ?? 'Bug-Report konnte nicht übermittelt werden.';
          return;
        }

        hint.textContent = resultBody.data?.message ?? 'Danke, dein Bug-Report wurde übermittelt.';
        form.reset();
      } catch {
        hint.textContent = 'Bug-Report konnte nicht übermittelt werden. Bitte später erneut versuchen.';
      } finally {
        submitBtn.disabled = false;
      }
    })();
  });
}

function bindContactForm(): void {
  const form = document.getElementById('contact-form') as HTMLFormElement | null;
  const submitBtn = document.getElementById('contact-submit-btn') as HTMLButtonElement | null;
  const hint = document.getElementById('contact-hint');
  if (!form || !submitBtn || !hint) return;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      submitBtn.disabled = true;
      hint.textContent = '';

      const category = (document.getElementById('contact-category') as HTMLSelectElement).value;
      const email = (document.getElementById('contact-email') as HTMLInputElement).value;
      const subject = (document.getElementById('contact-subject') as HTMLInputElement).value;
      const message = (document.getElementById('contact-message') as HTMLTextAreaElement).value;
      const website = (document.getElementById('contact-website') as HTMLInputElement).value;

      try {
        const result = await api.post<{ message: string }>('/support/contact', {
          website,
          category,
          email,
          subject,
          message,
        });
        hint.textContent = result.message;
        form.reset();
      } catch (err) {
        hint.textContent = err instanceof ApiError ? err.message : 'Nachricht konnte nicht übermittelt werden. Bitte später erneut versuchen.';
      } finally {
        submitBtn.disabled = false;
      }
    })();
  });
}

function bindFormsSection(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-forms-toggle]').forEach((header) => {
    header.addEventListener('click', () => {
      const id = header.dataset.formsToggle as FormsEntryId;
      openFormEntryId = openFormEntryId === id ? null : id;
      updateFormsEntryVisibility();
    });
  });
  bindBugReportForm();
  bindContactForm();
  bindPrivacyExport();
  bindPrivacyDelete();
}

export async function renderSettings(): Promise<void> {
  addStyles();
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div class="settings-page">
      <button class="settings-back" id="settings-back-btn">Zurück</button>
      <h1 class="settings-title">Einstellungen</h1>
      ${getFlashHtml()}
      <div id="settings-twitch-section">Lade...</div>
      <div class="settings-section">
        <div class="settings-section-title">Ton</div>
        <div id="settings-sound-section"></div>
      </div>
      <div class="settings-section">
        <div class="settings-section-title">Benachrichtigungen</div>
        <div id="settings-push-section">Lade...</div>
      </div>
      ${renderPrestigeThemeSection()}
      ${renderDiscordInviteSection()}
      <div id="settings-forms-section"></div>
    </div>
  `;

  document.getElementById('settings-back-btn')?.addEventListener('click', () => {
    navigate('/dashboard');
  });

  // URL-Parameter nach Auswertung entfernen
  cleanUrlParams();

  // FORMULARE-Sektion (kein Netzwerk-Call nötig, sofort rendern)
  openFormEntryId = null;
  pushCategoriesExpanded = false;
  const formsSection = document.getElementById('settings-forms-section');
  if (formsSection) {
    formsSection.innerHTML = renderFormsSection();
    bindFormsSection();
  }

  bindPrestigeThemeSection();

  // Sound-Lautstärke (rein lokal, kein Netzwerk-Call nötig)
  const soundSection = document.getElementById('settings-sound-section');
  if (soundSection) {
    soundSection.innerHTML = renderSoundSectionBody();
    bindSoundSection();
  }

  // Push-Prefs laden (NT-PUSH-PREFS-UI-01)
  const pushSection = document.getElementById('settings-push-section');
  if (pushSection) {
    try {
      const res = await api.get<{ prefs: PushPrefs }>('/push/prefs');
      pushPrefsState = res.prefs;
      pushSection.innerHTML = renderPushSectionBody(pushPrefsState);
      bindPushSection();
    } catch {
      pushSection.innerHTML = '<p class="settings-meta">Benachrichtigungs-Einstellungen konnten nicht geladen werden.</p>';
    }
  }

  // Twitch-Status laden
  const section = document.getElementById('settings-twitch-section');
  if (!section) return;

  try {
    const res = await api.get<TwitchStatus>('/auth/twitch/status');
    if (res.connected) {
      section.innerHTML = renderTwitchConnected(res.connectedAt);
      bindTwitchConnected();
    } else {
      section.innerHTML = renderTwitchDisconnected();
      bindTwitchDisconnected();
    }
  } catch {
    section.innerHTML = `<div class="settings-section"><p class="settings-meta">Status konnte nicht geladen werden.</p></div>`;
  }
}
